import { useState } from "react";
import type { SubscriptionViewModel } from "../../subscriptionView";
import { LinkButton } from "./LinkButton";

const STATUS_LABEL_KEY: Record<SubscriptionViewModel["userStatus"], string> = {
  ACTIVE: "active",
  DISABLED: "inactive",
  EXPIRED: "expired",
  LIMITED: "unknown",
};

const STATUS_TONE_CLASS: Record<SubscriptionViewModel["statusTone"], string> = {
  success: "text-accent-green",
  warning: "text-accent-yellow",
  danger: "text-accent-red",
};

// Фиксированный ASCII-бар (20 символов) хорош, пока блок держит свою обычную content-width —
// но в узкой/перенесённой раскладке блок растягивается на всю ширину карточки, а бар остаётся
// тем же 20-символьным огрызком. Здесь бар рисуется по факту доступной ширины: заливка/пустота —
// это flex-доли (%) от ratio, без замера в JS, вместо жёстко фиксированного числа символов.
function DynamicBar({ ratio, className }: { ratio: number; className?: string }) {
  const filledPercent = Math.min(100, Math.max(0, ratio * 100));
  return (
    <div className={`flex w-full whitespace-pre ${className ?? ""}`}>
      [
      <span className="flex flex-1 overflow-hidden">
        <span className="shrink-0 overflow-hidden whitespace-pre" style={{ width: `${filledPercent}%` }}>
          {"=".repeat(300)}
        </span>
        <span className="shrink-0 overflow-hidden whitespace-pre" style={{ width: `${100 - filledPercent}%` }}>
          {" ".repeat(300)}
        </span>
      </span>
      ]
    </div>
  );
}

export function SubscriptionCard({
  me,
  labels,
}: {
  me: SubscriptionViewModel;
  labels: Record<string, string>;
}) {
  const statusLabel = labels[STATUS_LABEL_KEY[me.userStatus]] ?? me.userStatus;
  const [copied, setCopied] = useState(false);

  function copyLink() {
    navigator.clipboard
      ?.writeText(me.subscriptionUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  }

  return (
    <div className="border border-border p-4">
      <div className="@container mb-4">
        <div className="flex items-center justify-between gap-3">
          <span className="whitespace-nowrap">
            <span className="font-bold">{me.username}</span>
            <span className="hidden @[420px]:inline">
              {" ["}
              <span className={STATUS_TONE_CLASS[me.statusTone]}>{statusLabel}</span>]
            </span>
          </span>
          <LinkButton active={copied} onClick={copyLink}>
            {copied ? labels.linkCopied : labels.copyLink}
          </LinkButton>
        </div>
        <div className="text-text-muted @[420px]:hidden">
          {labels.status}: <span className={STATUS_TONE_CLASS[me.statusTone]}>{statusLabel}</span>
        </div>
      </div>
      <div className="@container flex flex-wrap gap-6">
        <div className="w-full @[420px]:w-auto">
          <div className="text-text-muted">{labels.expires}</div>
          <div className="whitespace-nowrap">{new Date(me.expiresAt).toLocaleDateString()}</div>
          <div className="hidden @[420px]:block whitespace-pre">{me.expiryProgressBar}</div>
          <DynamicBar ratio={me.expiryFilledRatio} className="@[420px]:hidden" />
        </div>
        <div className="w-full @[420px]:w-auto @[420px]:ml-auto @[420px]:text-right">
          <div className="text-text-muted">{labels.bandwidth}</div>
          <div className="whitespace-nowrap">{me.trafficDisplay}</div>
          <div className="hidden @[420px]:block whitespace-pre">{me.trafficProgressBar}</div>
          <DynamicBar ratio={me.trafficFilledRatio} className="@[420px]:hidden" />
        </div>
      </div>
    </div>
  );
}
